import { useState } from 'preact/hooks';

/**
 * Promo-code entry on the Payment step.
 *
 * The field is collapsed behind a link by default. A visible "enter a
 * code" box on a checkout screen makes every customer without one feel
 * they are paying too much, and sends a few off to search for codes
 * instead of booking — so it stays out of the way until someone actually
 * has one.
 *
 * Applying re-fetches the cart AND the deposit together, because a code
 * changes what the card is about to be charged. Showing a new subtotal
 * against a stale deposit would be the one genuinely misleading state
 * here.
 */
export function PromoCode({
  appliedCode,
  discountCents,
  currency,
  onApply,
  onRemove,
  disabled,
  formatMoney,
}: {
  appliedCode: string | null;
  discountCents: number;
  currency: string;
  onApply: (code: string) => Promise<{ ok: boolean; message?: string }>;
  onRemove: () => Promise<void>;
  disabled?: boolean;
  /** Payment owns the money formatter; passed in rather than duplicated. */
  formatMoney: (cents: number, currency: string) => string;
}) {
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    const code = value.trim();
    if (!code || busy) return;
    setBusy(true);
    setError(null);
    try {
      const r = await onApply(code);
      if (!r.ok) {
        setError(r.message ?? "That code isn't valid.");
        return;
      }
      setValue('');
    } catch {
      setError('We could not check that code. Try again.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      await onRemove();
      setOpen(false);
    } catch {
      setError('We could not remove that code. Try again.');
    } finally {
      setBusy(false);
    }
  }

  if (discountCents > 0) {
    return (
      <div class="sbw-deposit-row">
        <span>
          {appliedCode ? (
            <>
              Code <strong>{appliedCode}</strong> applied
            </>
          ) : (
            'Promo code applied'
          )}
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontVariantNumeric: 'tabular-nums' }}>
            &minus;{formatMoney(discountCents, currency)}
          </span>
          <button
            type="button"
            onClick={remove}
            disabled={busy || disabled}
            aria-label={appliedCode ? `Remove code ${appliedCode}` : 'Remove promo code'}
            class="sbw-promo-link"
          >
            Remove
          </button>
        </span>
      </div>
    );
  }

  if (!open) {
    return (
      <div class="sbw-deposit-row">
        <button
          type="button"
          class="sbw-promo-link"
          onClick={() => setOpen(true)}
          disabled={disabled}
        >
          Have a promo code?
        </button>
      </div>
    );
  }

  // A DIV, not a form, and an explicit type="button".
  //
  // PaymentStep is itself `<form onSubmit={handlePay}>`, and nested forms
  // are invalid HTML — the browser drops the inner one, so a submit
  // button here would submit the OUTER form and ATTEMPT THE PAYMENT.
  // Enter is handled by hand for the same reason.
  return (
    <div class="sbw-promo">
      <div class="sbw-promo-controls">
        <input
          value={value}
          onInput={(e) => setValue((e.target as HTMLInputElement).value)}
          onKeyDown={(e) => {
            if ((e as KeyboardEvent).key !== 'Enter') return;
            // Stop it reaching the payment form's implicit submit.
            e.preventDefault();
            e.stopPropagation();
            void submit();
          }}
          placeholder="Promo code"
          aria-label="Promo code"
          autocapitalize="characters"
          autocomplete="off"
          disabled={busy || disabled}
          class="sbw-promo-input"
        />
        <button
          type="button"
          onClick={() => void submit()}
          disabled={busy || disabled || !value.trim()}
          class="sbw-promo-btn"
        >
          {busy ? 'Checking…' : 'Apply'}
        </button>
      </div>
      {error && (
        <span role="alert" class="sbw-promo-err">
          {error}
        </span>
      )}
    </div>
  );
}
